import { useQuery } from '@tanstack/react-query'
import {
  analyticsFetch,
  apiFetch,
  ApiError,
  getCallerAgentId,
  hasLiveAnalytics,
  hasLiveApi,
} from '@/api/client'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'
import {
  emptyLiveOverview,
  fromFixtureOverview,
  previousRange,
  toOverviewView,
  type AnalyticsOverviewSnapshot,
  type OverviewView,
} from '@/api/map/overview'
import type { AnalyticsTurnoverResponse } from '@/api/map/turnover'
import type { AnalyticsTransfersPage } from '@/api/map/transfers'
import * as api from '@/data/api'
import type { DateRange } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

function rangeQuery(range: { from: string; to: string }): string {
  return new URLSearchParams({
    from: range.from.slice(0, 10),
    to: range.to.slice(0, 10),
  }).toString()
}

function softFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 502 || err.status === 503 || err.status === 504 || err.status === 0) {
      return 'Analytics is unreachable right now. Sections below show No data found until it responds.'
    }
    return err.message || `Request failed (${err.status})`
  }
  if (err instanceof Error && err.message) return err.message
  return 'Analytics is unreachable right now. Sections below show No data found until it responds.'
}

/** Resolve the acting account for free-to-give / credit KPIs (hierarchy or fixture). */
async function loadActingAccount(agentId: string) {
  if (hasLiveApi()) {
    try {
      const node = await apiFetch<AgentNode>(`/v1/agents/${encodeURIComponent(agentId)}`)
      return agentNodeToAccount(node)
    } catch {
      /* hierarchy optional for analytics-only overview */
    }
  }
  try {
    const session = await api.getSession()
    return (await api.getAccount(session.accountId)) ?? null
  } catch {
    return null
  }
}

/**
 * Live Overview = OpenAPI GET /overview (+ prior window, turnover agents, transfers).
 * Overview 502/network → empty shell with per-section No data found (never blank the page).
 * Never silently swaps Live numbers for fixtures.
 */
async function loadOverviewLive(range: DateRange): Promise<OverviewView> {
  const id = getCallerAgentId()
  if (!id) {
    throw new Error('Live analytics needs a Keycloak session with an agentId claim')
  }

  const qs = rangeQuery(range)
  const prior = previousRange(range.from, range.to)
  const priorQs = rangeQuery(prior)

  const [currentResult, previous, turnover, transfers, account] = await Promise.all([
    analyticsFetch<AnalyticsOverviewSnapshot>(
      `/v1/agents/${encodeURIComponent(id)}/overview?${qs}`,
    )
      .then((data) => ({ ok: true as const, data }))
      .catch((err: unknown) => ({ ok: false as const, err })),
    analyticsFetch<AnalyticsOverviewSnapshot>(
      `/v1/agents/${encodeURIComponent(id)}/overview?${priorQs}`,
    ).catch(() => null),
    analyticsFetch<AnalyticsTurnoverResponse>(
      `/v1/agents/${encodeURIComponent(id)}/turnover?${qs}&groupBy=agent`,
    ).catch(() => null),
    analyticsFetch<AnalyticsTransfersPage>(
      `/v1/agents/${encodeURIComponent(id)}/transfers?${qs}&page=0&size=8`,
    ).catch(() => null),
    loadActingAccount(id),
  ])

  const companions = {
    account,
    agentTurnoverRows: turnover ? (turnover.rows ?? []) : null,
    transferRows: transfers ? (transfers.rows ?? []) : null,
    selfId: id,
  }

  if (!currentResult.ok) {
    return emptyLiveOverview({
      ...companions,
      loadError: softFailMessage(currentResult.err),
    })
  }

  return toOverviewView({
    current: currentResult.data,
    previous,
    ...companions,
  })
}

async function loadOverview(query: api.OverviewQuery): Promise<OverviewView> {
  if (hasLiveAnalytics()) return loadOverviewLive(query.range)
  const snapshot = await api.getOverview(query)
  if (!snapshot) {
    throw new Error('That scope is not beneath you')
  }
  return fromFixtureOverview(snapshot)
}

export function useOverview(query: api.OverviewQuery) {
  return useQuery({
    queryKey: keys.overview(query),
    queryFn: () => loadOverview(query),
    refetchInterval: api.POLL_INTERVAL_MS,
    placeholderData: (previous) => previous,
  })
}
